import React from 'react'

import { useUpdateLog } from 'utils/log'

import { FormTitleProps } from '../items/FormTitle'
import SelectImageLine from '../items/SelectImageLine'
import BaseGroup from './BaseGroup'

import { AccidentImage, AccidentImageKeys, ImageInfo } from 'states/QnState'
import { VEHICLE_STATE_FORM } from 'constants/qnState'
import AccidentDefaultImage1 from 'images/accident-default-image1.png'
import AccidentDefaultImage2 from 'images/accident-default-image2.png'

/**
 * 対象がSelectImageGroupProps型かチェックする
 * @param target チェック対象
 */
export const isSelectImageGroupProps = (
  target: any
): target is SelectImageGroupProps => {
  return target.images !== undefined && target.title !== undefined
}

export type SelectImageGroupProps = FormTitleProps & {
  accidentImage: AccidentImage
  onChanged: (name: AccidentImageKeys, imageInfo: ImageInfo) => void
}

/**
 * 損傷画像を入力するコンポーネント
 * @param props SelectImageGroupProps
 * @returns
 */
export default function SelectImageGroup(
  props: SelectImageGroupProps
): JSX.Element {
  const {
    title,
    description,
    isRequired,
    hideTitle,
    accidentImage,
    onChanged,
  } = props

  const handleImageChanged = React.useCallback(
    (name: AccidentImageKeys, image: string) => {
      if (onChanged) {
        onChanged(name, { ...accidentImage[name], image })
      }
    },
    [accidentImage]
  )

  const handleOrientationChanged = React.useCallback(
    (name: AccidentImageKeys, orientation: number) => {
      if (onChanged) {
        onChanged(name, { ...accidentImage[name], orientation })
      }
    },
    [accidentImage]
  )

  useUpdateLog(
    props.title,
    {
      title: props.title,
      isRequired: props.isRequired,
      accidentImage: props.accidentImage,
    },
    [props]
  )

  return (
    <div style={{ height: '400px' }}>
      {
        <BaseGroup
          title={title}
          description={description}
          hideTitle={hideTitle}
        >
          <SelectImageLine
            image={accidentImage.peripheral.image}
            isRequired={isRequired}
            orientation={accidentImage.peripheral.orientation}
            defaultImage={AccidentDefaultImage1}
            name={VEHICLE_STATE_FORM.accidentImage.options.peripheral}
            isSupplement={false}
            setImage={(image): void => handleImageChanged('peripheral', image)}
            setOrientation={(orientation): void => {
              handleOrientationChanged('peripheral', orientation)
            }}
          />
          <SelectImageLine
            image={accidentImage.damagedPart.image}
            isRequired={isRequired}
            orientation={accidentImage.damagedPart.orientation}
            defaultImage={AccidentDefaultImage2}
            name={VEHICLE_STATE_FORM.accidentImage.options.damagedPart}
            isSupplement={false}
            setImage={(image): void => handleImageChanged('damagedPart', image)}
            setOrientation={(orientation): void => {
              handleOrientationChanged('damagedPart', orientation)
            }}
          />
          <>
            <SelectImageLine
              image={accidentImage.supplement.image}
              orientation={accidentImage.supplement.orientation}
              name={VEHICLE_STATE_FORM.accidentImage.options.supplement}
              isSupplement={true}
              setImage={(image): void =>
                handleImageChanged('supplement', image)
              }
              setOrientation={(orientation): void => {
                handleOrientationChanged('supplement', orientation)
              }}
            />
            <div style={{ marginBottom: '10px' }} />
            {/* 補足写真を選択ボタンの下にマージンを開ける */}
            {!accidentImage.supplement.image && (
              <div style={{ marginBottom: '30px' }} />
            )}
          </>
        </BaseGroup>
      }
    </div>
  )
}
